import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Settings, History, Download, X, RotateCcw } from 'lucide-react';
import { supabase } from './supabaseClient';
import RepSelector from './RepSelector';

// Returns today's date as YYYY-MM-DD in Pacific time (auto-handles PST/PDT)
const getLocalDate = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });

// Returns today's date as a string key for daily-reset comparisons (also Pacific time)
const getLocalDateKey = () =>
  new Date().toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles' });

// Maps tally metric names to daily_entries column names
const METRIC_TO_COLUMN = {
  // Closer
  closer_sale_made:           'sales_made',
  closer_calls_taken:         'sales_calls_taken',
  closer_followup_booked:     'followup_booked',
  closer_followup_taken:      'followup_taken',
  // Setter
  setter_dms_sent:            'dms_sent',
  setter_convos_started:      'convos_started',
  setter_meaningful_convos:   'meaningful_convos',
  setter_discovery_offered:   'discovery_offered',
  setter_discovery_booked:    'discovery_booked',
  setter_sales_calls_booked:  'sales_calls_booked',
  setter_sales_calls_offered: 'sales_calls_offered',
  setter_friend_requests:     'friend_requests',
  setter_group_joins:         'group_joins',
  setter_organic_leads:       'organic_leads',
  setter_sms_convo:           'sms_convo',
  setter_email_convo:         'email_convo',
  setter_new_offer:           'offers_made',
  // VA
  va_friend_requests:         'friend_requests',
  va_group_adds:              'group_adds',
  va_welcome_messages:        'welcome_messages',
  va_join_requests:           'join_requests',
  va_admin_tasks:             'admin_tasks',
  // CSM
  csm_active_clients:         'active_clients',
  csm_new_clients:            'new_clients',
  csm_strategic_reviews:      'strategic_reviews',
  csm_cs_dev_calls:           'cs_dev_calls',
  csm_post_launch_calls:      'post_launch_calls',
  csm_offers_made:            'offers_made',
  csm_clients_lost:           'clients_lost',
}

// For garrett, route closer_ metrics to garrett_closer, setter_ to garrett_setter
function resolveRepId(repId, metric) {
  if (repId === 'garrett') {
    if (metric.startsWith('closer_')) return 'garrett_closer'
    if (metric.startsWith('setter_')) return 'garrett_setter'
  }
  return repId
}

export default function TallyCounterApp() {
  const [counters, setCounters] = useState([]);
  const [history, setHistory] = useState([]);
  const [view, setView] = useState('main');
  const [repId, setRepId] = useState(null);
  const [repRole, setRepRole] = useState(null);
  const [settings, setSettings] = useState({
    vibration: true,
    showTotal: true,
    flashScreen: true,
    compactMode: false,
  });
  const [filter, setFilter] = useState('today');
  const [counterFilter, setCounterFilter] = useState('today');
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [flashColor, setFlashColor] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [saleModal, setSaleModal] = useState({ open: false, counterId: null });

  const totalSaleRef = useRef(null);
  const cashCollectedRef = useRef(null);

  const roleColors = {
    setter: '#F97316',
    closer: '#EF4444',
    csm:    '#8B5CF6',
    va:     '#22C55E',
    mkt:    '#0EA5E9',
  };

  const metricColors = {
    setter_new_offer:           '#F97316',
    setter_friend_requests:     '#FB923C',
    setter_group_joins:         '#FBBF24',
    setter_organic_leads:       '#FCD34D',
    setter_sms_convo:           '#A3E635',
    setter_email_convo:         '#34D399',
    setter_dms_sent:            '#22D3EE',
    setter_convos_started:      '#38BDF8',
    setter_meaningful_convos:   '#60A5FA',
    setter_discovery_offered:   '#818CF8',
    setter_discovery_booked:    '#A78BFA',
    setter_discovery_taken:     '#C084FC',
    setter_sales_calls_offered: '#E879F9',
    setter_sales_calls_booked:  '#F472B6',
    closer_calls_taken:         '#FB7185',
    closer_followup_booked:     '#F43F5E',
    closer_followup_taken:      '#E11D48',
    closer_sale_made:           '#BE123C',
  };

  const getMetricColor = (metric, index, role) => {
    if (metricColors[metric]) return metricColors[metric];
    const fallback = Object.values(roleColors);
    return roleColors[role] || fallback[index % fallback.length];
  };

  const loadMetricsForRep = useCallback(async (id, role) => {
    try {
      const roles = role === 'garrett' ? ['setter', 'closer'] : [role];

      const { data, error } = await supabase
        .from('goals')
        .select('metric, label')
        .eq('tally_enabled', true)
        .order('sort_order');

      if (error) {
        console.error('Supabase error:', error);
        return;
      }

      const filtered = (data || []).filter(row =>
        roles.some(r => row.metric.startsWith(`${r}_`))
      );

      if (filtered.length > 0) {
        const today = getLocalDateKey();
        const newCounters = filtered.map((row, i) => ({
          id: row.metric,
          name: row.label || row.metric,
          metric: row.metric,
          count: 0,
          todayCount: 0,
          color: getMetricColor(row.metric, i, role),
          createdAt: new Date().toISOString(),
          lastResetDate: today,
        }));

        const saved = localStorage.getItem(`tallyCounters_${id}`);
        if (saved) {
          const savedCounters = JSON.parse(saved);
          const merged = newCounters.map(c => {
            const existing = savedCounters.find(s => s.id === c.id);
            return existing
              ? { ...c, count: existing.count, todayCount: existing.todayCount, lastResetDate: existing.lastResetDate }
              : c;
          });
          setCounters(checkAndResetDailyCounts(merged));
        } else {
          setCounters(newCounters);
        }
      }
    } catch (e) {
      console.error('Load metrics failed:', e);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRepSelect = useCallback((id, role) => {
    setRepId(id);
    setRepRole(role);
    loadMetricsForRep(id, role);
  }, [loadMetricsForRep]);

  useEffect(() => {
    const savedHistory = localStorage.getItem('tallyHistory');
    const savedSettings = localStorage.getItem('tallySettings');
    if (savedHistory) setHistory(JSON.parse(savedHistory));
    if (savedSettings) setSettings(prev => ({ ...prev, ...JSON.parse(savedSettings) }));
  }, []);

  useEffect(() => {
    if (repId) localStorage.setItem(`tallyCounters_${repId}`, JSON.stringify(counters));
  }, [counters, repId]);

  useEffect(() => { localStorage.setItem('tallyHistory', JSON.stringify(history)); }, [history]);
  useEffect(() => { localStorage.setItem('tallySettings', JSON.stringify(settings)); }, [settings]);

  const checkAndResetDailyCounts = (list) => {
    const today = getLocalDateKey();
    return list.map(counter => {
      const lastReset = counter.lastResetDate || getLocalDateKey();
      if (lastReset !== today) return { ...counter, todayCount: 0, lastResetDate: today };
      if (counter.todayCount === undefined) return { ...counter, todayCount: 0, lastResetDate: today };
      return counter;
    });
  };

  const logTap = async (metric, delta, extraFields = {}) => {
    if (!repId) return;

    const column = METRIC_TO_COLUMN[metric];
    if (!column) {
      console.warn('No column mapping for metric:', metric);
      return;
    }

    const targetRepId = resolveRepId(repId, metric);
    const today = getLocalDate();

    // First get the current value so we can increment/decrement it
    const { data: existing } = await supabase
      .from('daily_entries')
      .select(column)
      .eq('rep_id', targetRepId)
      .eq('entry_date', today)
      .maybeSingle();

    const currentValue = existing ? (parseFloat(existing[column]) || 0) : 0;
    const newValue = Math.max(0, currentValue + delta);

    const upsertData = {
      rep_id: targetRepId,
      entry_date: today,
      [column]: newValue,
      ...extraFields,
    };

    await supabase
      .from('daily_entries')
      .upsert(upsertData, { onConflict: 'rep_id,entry_date' });
  };

  const incrementCounter = (id, extraFields = {}) => {
    const counter = counters.find(c => c.id === id);
    if (settings.vibration && navigator.vibrate) navigator.vibrate(10);
    if (settings.flashScreen) triggerFlash(counter.color);
    setCounters(prev => prev.map(c =>
      c.id === id ? { ...c, count: c.count + 1, todayCount: (c.todayCount || 0) + 1 } : c
    ));
    addToHistory(counter.name, 1);
    logTap(counter.metric, 1, extraFields);
  };

  const decrementCounter = (id) => {
    const counter = counters.find(c => c.id === id);
    if (settings.flashScreen) triggerFlash(counter.color);
    setCounters(prev => prev.map(c =>
      c.id === id ? {
        ...c,
        count: Math.max(0, c.count - 1),
        todayCount: Math.max(0, (c.todayCount || 0) - 1)
      } : c
    ));
    addToHistory(counter.name, -1);
    logTap(counter.metric, -1);
  };

  const handleIncrementTap = (id) => {
    const counter = counters.find(c => c.id === id);
    if (counter && counter.metric === 'closer_sale_made') {
      setSaleModal({ open: true, counterId: id });
    } else {
      incrementCounter(id);
    }
  };

  const handleSaleConfirm = () => {
    const totalSale = parseFloat(totalSaleRef.current?.value) || null;
    const cashCollected = parseFloat(cashCollectedRef.current?.value) || null;
    incrementCounter(saleModal.counterId, {
      total_sale_amount: totalSale,
      cash_collected: cashCollected,
    });
    setSaleModal({ open: false, counterId: null });
  };

  const handleSaleCancel = () => {
    setSaleModal({ open: false, counterId: null });
  };

  const triggerFlash = (color) => {
    setFlashColor(color);
    setTimeout(() => setFlashColor(null), 200);
  };

  const showConfirm = (message) => {
    return new Promise((resolve) => {
      setConfirmDialog({
        message,
        onConfirm: () => { setConfirmDialog(null); resolve(true); },
        onCancel:  () => { setConfirmDialog(null); resolve(false); }
      });
    });
  };

  const addToHistory = (name, change) => {
    setHistory(prev => [{ id: Date.now(), name, change, timestamp: new Date().toISOString() }, ...prev]);
  };

  const resetAllCounters = async () => {
    const confirmed = await showConfirm('Reset all counters to 0?');
    if (confirmed) setCounters(prev => prev.map(c => ({ ...c, count: 0, todayCount: 0 })));
  };

  const exportData = () => {
    const blob = new Blob([JSON.stringify({ counters, history, exportDate: new Date().toISOString() }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tally-export-${getLocalDate()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getDisplayCount = (counter) => counterFilter === 'today' ? (counter.todayCount || 0) : counter.count;
  const getTotalCount = () => counterFilter === 'today'
    ? counters.reduce((sum, c) => sum + (c.todayCount || 0), 0)
    : counters.reduce((sum, c) => sum + c.count, 0);
  const getAverageCount = () => counters.length === 0 ? 0 : (getTotalCount() / counters.length).toFixed(2);

  const getFilteredHistory = () => {
    if (filter === 'all') return history;
    const todayKey = getLocalDateKey();
    return history.filter(h =>
      new Date(h.timestamp).toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles' }) === todayKey
    );
  };

  const formatDate = (dateString) => {
    const dateKey = new Date(dateString).toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles' });
    const todayKey = getLocalDateKey();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayKey = yesterday.toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles' });
    if (dateKey === todayKey) return 'TODAY';
    if (dateKey === yesterdayKey) return 'YESTERDAY';
    return new Date(dateString).toLocaleDateString('en-US', {
      timeZone: 'America/Los_Angeles',
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    }).toUpperCase();
  };

  const groupHistoryByDate = () => {
    const grouped = {};
    getFilteredHistory().forEach(entry => {
      const key = new Date(entry.timestamp).toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles' });
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(entry);
    });
    return grouped;
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    setTimeout(() => { e.target.style.opacity = '0.5'; }, 0);
  };
  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedIndex(null); setDragOverIndex(null);
  };
  const handleDragOver = (e, index) => {
    e.preventDefault(); e.dataTransfer.dropEffect = 'move';
    if (draggedIndex !== null && draggedIndex !== index) setDragOverIndex(index);
  };
  const handleDragLeave = () => setDragOverIndex(null);
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) { setDragOverIndex(null); return; }
    const next = [...counters];
    const [moved] = next.splice(draggedIndex, 1);
    next.splice(dropIndex, 0, moved);
    setCounters(next);
    setDraggedIndex(null); setDragOverIndex(null);
  };

  const ConfirmDialog = () => !confirmDialog ? null : (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 10000, padding: '20px'
    }}>
      <div style={{
        background: '#1E293B', borderRadius: '20px', padding: '24px',
        maxWidth: '400px', width: '100%',
        border: '1px solid rgba(148,163,184,0.2)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
      }}>
        <p style={{ margin: '0 0 24px 0', fontSize: '16px', lineHeight: '1.5', color: '#F8FAFC', textAlign: 'center' }}>
          {confirmDialog.message}
        </p>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={confirmDialog.onCancel} style={{
            flex: 1, padding: '12px', background: 'rgba(148,163,184,0.1)',
            border: '1px solid rgba(148,163,184,0.3)', borderRadius: '12px',
            color: '#F8FAFC', fontSize: '15px', fontWeight: '600', cursor: 'pointer'
          }}>Cancel</button>
          <button onClick={confirmDialog.onConfirm} style={{
            flex: 1, padding: '12px',
            background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
            border: 'none', borderRadius: '12px', color: '#FFFFFF',
            fontSize: '15px', fontWeight: '600', cursor: 'pointer'
          }}>Confirm</button>
        </div>
      </div>
    </div>
  );

  const SaleModal = () => !saleModal.open ? null : (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 10000, padding: '20px'
    }}>
      <div style={{
        background: '#1E293B', borderRadius: '24px', padding: '28px',
        maxWidth: '400px', width: '100%',
        border: '1px solid rgba(190,18,60,0.4)',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#F8FAFC' }}>🎉 Sale Made</h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#94A3B8' }}>Enter the deal details</p>
          </div>
          <button onClick={handleSaleCancel} style={{
            background: 'rgba(148,163,184,0.1)', border: '1px solid rgba(148,163,184,0.2)',
            borderRadius: '10px', padding: '8px', cursor: 'pointer', color: '#94A3B8',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}><X size={18} /></button>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#94A3B8', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Total Sale Amount
          </label>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', fontWeight: '600', color: '#64748B' }}>$</span>
            <input
              autoFocus
              ref={totalSaleRef}
              type="number"
              inputMode="numeric"
              placeholder="0"
              style={{
                width: '100%', padding: '14px 14px 14px 28px',
                background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(148,163,184,0.2)',
                borderRadius: '12px', color: '#F8FAFC', fontSize: '18px', fontWeight: '600',
                outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '28px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#94A3B8', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Cash Collected
          </label>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px', fontWeight: '600', color: '#64748B' }}>$</span>
            <input
              ref={cashCollectedRef}
              type="number"
              inputMode="numeric"
              placeholder="0"
              style={{
                width: '100%', padding: '14px 14px 14px 28px',
                background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(148,163,184,0.2)',
                borderRadius: '12px', color: '#F8FAFC', fontSize: '18px', fontWeight: '600',
                outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={handleSaleCancel} style={{
            flex: 1, padding: '14px', background: 'rgba(148,163,184,0.1)',
            border: '1px solid rgba(148,163,184,0.3)', borderRadius: '12px',
            color: '#F8FAFC', fontSize: '15px', fontWeight: '600', cursor: 'pointer'
          }}>Cancel</button>
          <button onClick={handleSaleConfirm} style={{
            flex: 2, padding: '14px',
            background: 'linear-gradient(135deg, #BE123C 0%, #9F1239 100%)',
            border: 'none', borderRadius: '12px', color: '#FFFFFF',
            fontSize: '15px', fontWeight: '700', cursor: 'pointer'
          }}>Log Sale</button>
        </div>
      </div>
    </div>
  );

  if (view === 'main') return (
    <>
      <RepSelector key={repId || 'selector'} onSelect={handleRepSelect} />
      <ConfirmDialog />
      <SaleModal />
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
        color: '#F8FAFC', paddingBottom: '80px', position: 'relative'
      }}>
        {flashColor && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: flashColor, opacity: 0.3, pointerEvents: 'none', zIndex: 9999
          }}>
            <style>{`@keyframes flash { 0%{opacity:.5} 50%{opacity:.3} 100%{opacity:0} }`}</style>
          </div>
        )}
        <div style={{
          background: 'rgba(15,23,42,0.8)', backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(148,163,184,0.1)',
          padding: '20px', position: 'sticky', top: 0, zIndex: 100
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: '800px', margin: '0 auto' }}>
            <div>
              <h1 style={{
                margin: 0, fontSize: '28px', fontWeight: '700',
                background: 'linear-gradient(135deg, #60A5FA 0%, #A78BFA 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: '-0.5px'
              }}>Tally</h1>
              <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#94A3B8', fontWeight: '500' }}>
                Business KPI Tracker
                {repId && (
                  <span
                    onClick={() => {
                      localStorage.removeItem('tally_rep_id');
                      localStorage.removeItem('tally_rep_role');
                      setRepId(null);
                      setRepRole(null);
                      setCounters([]);
                    }}
                    style={{ marginLeft: '8px', color: '#60A5FA', cursor: 'pointer', fontSize: '12px' }}
                  >
                    ({repId.charAt(0).toUpperCase() + repId.slice(1)} · {repRole} · switch)
                  </span>
                )}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              {[
                { icon: <History size={20} />, action: () => setView('history') },
                { icon: <Settings size={20} />, action: () => setView('settings') },
              ].map((btn, i) => (
                <button key={i} onClick={btn.action} style={{
                  background: 'rgba(148,163,184,0.1)', border: '1px solid rgba(148,163,184,0.2)',
                  borderRadius: '12px', padding: '10px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F8FAFC'
                }}>{btn.icon}</button>
              ))}
            </div>
          </div>
        </div>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '16px 20px 0' }}>
          <div style={{ display: 'flex', gap: '8px', background: 'rgba(15,23,42,0.6)', borderRadius: '12px', padding: '4px' }}>
            {['all', 'today'].map(f => (
              <button key={f} onClick={() => setCounterFilter(f)} style={{
                flex: 1, padding: '10px 16px',
                background: counterFilter === f ? 'rgba(148,163,184,0.3)' : 'transparent',
                border: 'none', borderRadius: '8px',
                color: counterFilter === f ? '#F8FAFC' : '#64748B',
                fontSize: '15px', fontWeight: '600', cursor: 'pointer'
              }}>{f === 'all' ? 'All' : 'Today'}</button>
            ))}
          </div>
        </div>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
          {counters.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748B' }}>
              <p style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>
                {repId ? 'Loading your metrics...' : 'Select who you are to load your metrics.'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: settings.compactMode ? '2px' : '12px' }}>
              {counters.map((counter, index) => (
                <div
                  key={counter.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  style={{
                    background: settings.compactMode
                      ? `linear-gradient(135deg, ${counter.color}E6 0%, ${counter.color}CC 100%)`
                      : `linear-gradient(135deg, ${counter.color}D9 0%, ${counter.color}B3 100%)`,
                    border: dragOverIndex === index ? `2px solid ${counter.color}` : `1px solid ${counter.color}80`,
                    borderRadius: settings.compactMode ? '6px' : '16px',
                    padding: settings.compactMode ? '6px 12px' : '20px',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    position: 'relative', overflow: 'hidden', cursor: 'grab',
                    transform: dragOverIndex === index ? 'scale(1.02)' : 'scale(1)',
                    opacity: draggedIndex === index ? 0.5 : 1,
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{
                    position: 'absolute', left: 0, top: 0, bottom: 0,
                    width: settings.compactMode ? '3px' : '4px',
                    background: counter.color
                  }} />
                  <div style={{
                    flex: 1, paddingLeft: settings.compactMode ? '8px' : '12px',
                    display: 'flex', alignItems: 'center', gap: settings.compactMode ? '12px' : '0'
                  }}>
                    <h3 style={{
                      margin: 0, fontSize: settings.compactMode ? '14px' : '16px', fontWeight: '600',
                      color: '#FFFFFF', flex: settings.compactMode ? '0 0 auto' : '1',
                      textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                    }}>{counter.name}</h3>
                    {settings.compactMode ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                        <p style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#FFFFFF', minWidth: '32px', textAlign: 'right' }}>
                          {getDisplayCount(counter)}
                        </p>
                        {counterFilter === 'today' && counter.count > 0 && (
                          <p style={{ margin: 0, fontSize: '10px', color: 'rgba(255,255,255,0.5)' }}>({counter.count})</p>
                        )}
                      </div>
                    ) : (
                      <div>
                        <p style={{
                          margin: '4px 0 0 0', fontSize: '32px', fontWeight: '700', lineHeight: 1,
                          color: '#FFFFFF', textShadow: '0 1px 3px rgba(0,0,0,0.3)'
                        }}>{getDisplayCount(counter)}</p>
                        {counterFilter === 'today' && counter.count > 0 && (
                          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>Overall: {counter.count}</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: settings.compactMode ? '4px' : '8px', alignItems: 'center', flexShrink: 0 }}>
                    <button onClick={() => decrementCounter(counter.id)} style={{
                      width: settings.compactMode ? '28px' : '48px', height: settings.compactMode ? '28px' : '48px',
                      borderRadius: settings.compactMode ? '6px' : '12px',
                      border: '1px solid rgba(148,163,184,0.2)', background: 'rgba(15,23,42,0.4)',
                      color: '#F8FAFC', cursor: 'pointer', fontSize: settings.compactMode ? '16px' : '24px',
                      fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>−</button>
                    <button onClick={() => handleIncrementTap(counter.id)} style={{
                      width: settings.compactMode ? '28px' : '48px', height: settings.compactMode ? '28px' : '48px',
                      borderRadius: settings.compactMode ? '6px' : '12px', border: 'none',
                      background: counter.color, color: '#FFFFFF', cursor: 'pointer',
                      fontSize: settings.compactMode ? '16px' : '24px',
                      fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>+</button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {settings.showTotal && counters.length > 0 && (
            <div style={{
              marginTop: '20px', padding: '20px',
              background: 'rgba(148,163,184,0.05)', border: '1px solid rgba(148,163,184,0.1)',
              borderRadius: '16px', display: 'flex', justifyContent: 'space-around', gap: '20px'
            }}>
              {[
                { label: `Total ${counterFilter === 'today' ? 'Today' : ''}`, value: getTotalCount(), color: '#60A5FA' },
                { label: `Average ${counterFilter === 'today' ? 'Today' : ''}`, value: getAverageCount(), color: '#A78BFA' },
                { label: 'Metrics', value: counters.length, color: '#34D399' },
              ].map((stat, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#94A3B8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{stat.label}</p>
                  <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: stat.color }}>{stat.value}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );

  if (view === 'history') {
    const groupedHistory = groupHistoryByDate();
    return (
      <div style={{
        minHeight: '100vh', background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
        color: '#F8FAFC', paddingBottom: '40px'
      }}>
        <div style={{
          background: 'rgba(15,23,42,0.8)', backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(148,163,184,0.1)',
          padding: '20px', position: 'sticky', top: 0, zIndex: 100
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', maxWidth: '800px', margin: '0 auto' }}>
            <button onClick={() => setView('main')} style={{
              background: 'rgba(148,163,184,0.1)', border: '1px solid rgba(148,163,184,0.2)',
              borderRadius: '12px', padding: '10px', cursor: 'pointer', color: '#F8FAFC',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}><X size={20} /></button>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700' }}>Count History</h2>
          </div>
          <div style={{ display: 'flex', gap: '8px', maxWidth: '800px', margin: '16px auto 0' }}>
            {['today', 'all'].map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{
                flex: 1, padding: '10px 16px',
                background: filter === f ? 'linear-gradient(135deg, #60A5FA 0%, #A78BFA 100%)' : 'rgba(148,163,184,0.1)',
                border: filter === f ? 'none' : '1px solid rgba(148,163,184,0.2)',
                borderRadius: '10px', color: '#F8FAFC', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
              }}>{f === 'today' ? 'Today' : 'All Time'}</button>
            ))}
          </div>
        </div>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
          {Object.keys(groupedHistory).length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748B' }}>
              <History size={48} style={{ opacity: 0.5, marginBottom: '16px' }} />
              <p style={{ fontSize: '16px', fontWeight: '600', margin: 0 }}>No history yet</p>
            </div>
          ) : (
            Object.entries(groupedHistory).map(([date, entries]) => (
              <div key={date} style={{ marginBottom: '32px' }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '12px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: '1px' }}>
                  {formatDate(entries[0].timestamp)}
                </h3>
                <div style={{ background: 'rgba(148,163,184,0.05)', border: '1px solid rgba(148,163,184,0.1)', borderRadius: '16px', overflow: 'hidden' }}>
                  {entries.map((entry, index) => {
                    const counter = counters.find(c => c.name === entry.name);
                    const time = new Date(entry.timestamp).toLocaleTimeString('en-US', {
                      timeZone: 'America/Los_Angeles',
                      hour: 'numeric', minute: '2-digit', hour12: true
                    });
                    return (
                      <div key={entry.id} style={{
                        padding: '16px 20px',
                        borderBottom: index < entries.length - 1 ? '1px solid rgba(148,163,184,0.1)' : 'none',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: counter?.color || '#64748B' }} />
                          <div>
                            <p style={{ margin: '0 0 2px 0', fontSize: '15px', fontWeight: '600', color: '#F8FAFC' }}>{entry.name}</p>
                            <p style={{ margin: 0, fontSize: '13px', color: '#64748B' }}>{time}</p>
                          </div>
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: '700', color: entry.change > 0 ? '#34D399' : '#EF4444' }}>
                          {entry.change > 0 ? '+' : ''}{entry.change}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  if (view === 'settings') return (
    <>
      <ConfirmDialog />
      <div style={{
        minHeight: '100vh', background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
        color: '#F8FAFC', paddingBottom: '40px'
      }}>
        <div style={{
          background: 'rgba(15,23,42,0.8)', backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(148,163,184,0.1)',
          padding: '20px', position: 'sticky', top: 0, zIndex: 100
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', maxWidth: '800px', margin: '0 auto' }}>
            <button onClick={() => setView('main')} style={{
              background: 'rgba(148,163,184,0.1)', border: '1px solid rgba(148,163,184,0.2)',
              borderRadius: '12px', padding: '10px', cursor: 'pointer', color: '#F8FAFC',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}><X size={20} /></button>
            <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700' }}>Settings</h2>
          </div>
        </div>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
          <div style={{ background: 'rgba(148,163,184,0.05)', border: '1px solid rgba(148,163,184,0.1)', borderRadius: '16px', padding: '20px', marginBottom: '16px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '700' }}>⚙️ General</h3>
            {[
              { label: 'Vibration',             key: 'vibration' },
              { label: 'Flash Screen on Count', key: 'flashScreen' },
              { label: 'Compact Mode',           key: 'compactMode' },
              { label: 'Show Total & Average',   key: 'showTotal' },
            ].map(({ label, key }) => (
              <label key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', cursor: 'pointer' }}>
                <span style={{ fontSize: '15px', fontWeight: '600' }}>{label}</span>
                <div onClick={() => setSettings(prev => ({ ...prev, [key]: !prev[key] }))} style={{
                  width: '48px', height: '28px', borderRadius: '14px',
                  background: settings[key] ? 'linear-gradient(135deg, #60A5FA 0%, #A78BFA 100%)' : 'rgba(148,163,184,0.3)',
                  position: 'relative', transition: 'all 0.3s', cursor: 'pointer'
                }}>
                  <div style={{
                    width: '22px', height: '22px', borderRadius: '50%', background: '#FFFFFF',
                    position: 'absolute', top: '3px', left: settings[key] ? '23px' : '3px',
                    transition: 'all 0.3s', boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }} />
                </div>
              </label>
            ))}
          </div>
          <div style={{ background: 'rgba(148,163,184,0.05)', border: '1px solid rgba(148,163,184,0.1)', borderRadius: '16px', padding: '20px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '700' }}>💾 Data</h3>
            <button onClick={exportData} style={{
              width: '100%', padding: '14px', background: 'rgba(52,211,153,0.1)',
              border: '1px solid rgba(52,211,153,0.3)', borderRadius: '12px', color: '#34D399',
              fontSize: '15px', fontWeight: '600', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px'
            }}><Download size={18} /> Export All Data</button>
            <button onClick={resetAllCounters} style={{
              width: '100%', padding: '14px', background: 'rgba(251,146,60,0.1)',
              border: '1px solid rgba(251,146,60,0.3)', borderRadius: '12px', color: '#FB923C',
              fontSize: '15px', fontWeight: '600', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
            }}><RotateCcw size={18} /> Reset All Counters</button>
          </div>
        </div>
      </div>
    </>
  );
}
